import { generatePath, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { t, jt } from 'ttag';

import { refillBalance } from '@legacyUrls';
import { editProduct } from '@urls';
import { SUPPORT_FORM_URL } from '@constants';
import { useRegion } from '@hook/useRegion';
import { Link } from '@ui/Link';
import { ConfirmActionModal } from '@ui/ConfirmActionModal';

import s from './ConfirmSaleErrorModal.sss';

const ERROR_CODES = {
  SALE_CONFIRMATION_REQUIRED: 'sale_confirmation_required',
  NOT_ENOUGH_MONEY_FOR_CPA: 'not_enough_money_for_cpa',
  TOO_BIG_DISCOUNT: 'too_big_discount',
  DUPLICATE_ORDER_TRANSACTION: 'duplicate_order_transaction',
  PBR_ORDER: 'pbr_order',
  ERROR_CODE_DUPLICATE_MARK: 'error_code_duplicate_mark',
};

interface Props {
  isOpen: boolean,
  error?: GraphFieldErrorList | null,
  productId?: number,
  buyerUsername?: string,
  commission?: string | null,
  commissionPercent?: string | null,
  threadId?: number,
  orderId?: number | null,
  onApprove?: () => void,
  onClose: () => void,
  onSupport?: () => void,
}

export const ConfirmSaleErrorModal: React.FC<Props> = ({
  isOpen,
  error,
  productId,
  buyerUsername,
  commission,
  commissionPercent,
  threadId,
  orderId,
  onClose,
  onApprove,
  onSupport,
}) => {
  const history = useHistory();
  const { currencyName } = useRegion();

  const errorMessage = error?.messages[0];
  const errorMessageText = error?.messages[0].message;
  const errorPayload = error?.payload;
  let messages: (string | string[])[] = [];
  let confirmButtonText = null;
  const commisionText = commission ? `${commission} ${currencyName}` : `${commissionPercent}`;

  const SUPPORT_LINK = (
    <Link
      href={SUPPORT_FORM_URL}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onClick={onSupport}
    >
      {t`Відділ турботи`}
    </Link>
  );

  switch (errorMessage?.code) {
    case ERROR_CODES.SALE_CONFIRMATION_REQUIRED:
      messages = [
        t`Ви вже продавали цей товар користувачу ${buyerUsername}. Коли відмітите товар проданим, ми знімемо комісію ${commisionText}  (комісія залежить від вартості товару та типу акаунта)`,
        jt`Хочете відмітити продаж? У разі питань напишіть у ${SUPPORT_LINK}`,
      ];
      confirmButtonText = t`Відмітити проданим`;
      break;
    case ERROR_CODES.NOT_ENOUGH_MONEY_FOR_CPA:
      messages = [
        t`На вашому рахунку недостатньо коштів. Будь ласка, поповніть рахунок і спробуйте ще раз.`,
        t`На зарахування коштів може піти деякий час.`,
      ];
      confirmButtonText = t`Поповнити`;
      break;
    case ERROR_CODES.TOO_BIG_DISCOUNT:
      messages = [
        t`Ми помітили, що ви зробили покупцеві велику знижку.`,
        jt`Відредагуйте товар та вкажіть реальну ціну, за яку продали річ, або напишіть у ${SUPPORT_LINK}`,
      ];
      confirmButtonText = t`Редагувати`;
      break;
    case ERROR_CODES.DUPLICATE_ORDER_TRANSACTION:
    case ERROR_CODES.PBR_ORDER:
    case ERROR_CODES.ERROR_CODE_DUPLICATE_MARK:
      messages = [
        jt`Неможливо відмітити продаж. Спробуйте ще раз пізніше або напишіть у ${SUPPORT_LINK}`,
      ];
      confirmButtonText = t`Написати`;
      break;
    default:
      messages = [t`На жаль, сталась помилка "${errorMessageText}"`];
      confirmButtonText = t`Написати`;
  }

  const handleConfirm = async () => {
    if (errorMessage?.code === ERROR_CODES.SALE_CONFIRMATION_REQUIRED && onApprove) {
      await onApprove();
    } else if (errorMessage?.code === ERROR_CODES.NOT_ENOUGH_MONEY_FOR_CPA) {
      window.location.href = history.createHref({
        pathname: refillBalance,
        search: queryString.stringify({
          ...(orderId && {
            orderId,
          }),
          ...(threadId && {
            threadId,
          }),
          ...(errorPayload && {
            amount: errorPayload,
          }),
        }),
      });
    } else if (errorMessage?.code === ERROR_CODES.TOO_BIG_DISCOUNT && productId) {
      const editProductUrl = generatePath(editProduct, { productId });
      window.location.href = history.createHref({ pathname: editProductUrl });
    } else {
      if (onSupport) {
        onSupport();
      }
      window.open(SUPPORT_FORM_URL, '_blank')?.focus();
    }
  };

  return (
    (
      <ConfirmActionModal
        className={s.modal}
        isOpen={isOpen}
        withClose={false}
        confirmButtonText={confirmButtonText}
        cancelButtonText={t`Відміна`}
        onConfirm={handleConfirm}
        onRequestClose={onClose}
      >
        {messages.map((message, i) => (
          <p
            key={i}
            className={s.message}
          >
            {message}
          </p>
        ))}
      </ConfirmActionModal>
    )
  );
};
